import { Skeleton } from '@ui/components/ui/skeleton';
import { cn } from '@ui/lib/utils';

interface ActivityCardSkeletonProps {
  variant?: 'card' | 'list';
  className?: string;
}

export function ActivityCardSkeleton({ variant = 'card', className }: ActivityCardSkeletonProps) {
  if (variant === 'list') {
    return (
      <div
        className={cn(
          'flex items-center gap-3 md:gap-4 p-3 md:p-4 bg-white rounded-lg border border-gray-200',
          className
        )}
      >
        <Skeleton className="flex-shrink-0 w-20 h-20 rounded-lg" />
        <div className="flex-1 min-w-0 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
          <div className="flex gap-4">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
        <div className="flex-shrink-0 space-y-1">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-3 w-12" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'w-48 bg-card rounded-xl border border-border inline-flex flex-col justify-start items-start overflow-hidden',
        className
      )}
    >
      {/* Image skeleton - matches h-40 from ActivityCard */}
      <div className="self-stretch h-40 relative">
        <Skeleton className="w-full h-full" />
      </div>

      {/* Content skeleton - matches real card height: 111px (without bookings row) */}
      <div className="self-stretch p-3 flex flex-col justify-start items-start gap-2">
        {/* Title and professional name section */}
        <div className="self-stretch flex flex-col justify-start items-start gap-0.5">
          {/* Using exact text line heights from real card */}
          <Skeleton className="h-[22px] w-3/4" /> {/* text-base font-semibold leading-snug */}
          <Skeleton className="h-3 w-1/2" /> {/* text-xs leading-none */}
        </div>

        {/* Activity details section - 2 rows only (no bookings) to match 111px */}
        <div className="self-stretch flex flex-col justify-center items-start gap-[5px]">
          {/* Location row */}
          <div className="self-stretch inline-flex justify-start items-center gap-2">
            <Skeleton className="w-4 h-4 rounded flex-shrink-0" />
            <Skeleton className="h-4 w-16" /> {/* h-4 to match icon height */}
          </div>

          {/* Price row */}
          <div className="self-stretch inline-flex justify-start items-center gap-2">
            <Skeleton className="w-4 h-4 rounded flex-shrink-0" />
            <div className="flex gap-1 items-center">
              <Skeleton className="h-[18px] w-12" /> {/* text-sm leading-tight */}
              <Skeleton className="h-3 w-12" /> {/* text-xs */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
